import React, { useState, useEffect } from 'react'
import {
  Tools,
  ToastContext,
  Container,
  ListContainer,
  TextTitle,
  TextBody,
  HtmlContent,
  Button,
  Icon,
  NoSsr
} from '@pmi/pmi-iqos-ds-react'
import {
  updateCartCounter,
  getToastMessagesFromResponse,
  getToastMessagesFromError,
  mapProductListForDisplay,
  fillProductsWithExtraData,
  JWTDecode,
  isAuthenticated,
  checkStockNotification,
  isPurchaseLimitationsErrorCode,
  isCartPurchaseLimitationsErrorCode,
  extractErrorCode,
  parseTranslations,
  htmlTranslation,
  resetGrecaptcha,
  isProductEligibleToShow,
  CommonTranslations,
  setTranslations,
  cookie,
  analytics,
  constants,
  PublishedComponent,
  goTo,
  BazaarVoice,
  Routes,
  dispatchPopupPromotionAlreadyUsed,
  extractErrorMessage,
  canUseDOM,
  localstorage
} from '@pmi/pmi-iqos-shared-react'
import { api } from '@dcx-common/services'
import {
  ModalNotifyMe,
  ProductCardProduct,
  ProductListFilters
} from '@dcx-common/components'
import ProductListTranslations from './product-list.json'

const filterProductsToHide = products => {
  if (!products || !products.length) return []
  return products.filter(p => isProductEligibleToShow(p, cookie))
}

const ProductListComponent = ({
  category,
  translations,
  defaultProducts,
  listname,
  listBanner,
  mainCategory,
  filtersInfo = {},
  logError
}) => {
  const [products, setProducts] = useState(
    filterProductsToHide(defaultProducts)
  )
  const [loadingExtraData, setLoadingExtraData] = useState(true)
  const [secondHalf, setSecondHalf] = useState(true)
  const [modalNotifyMe, setModalNotifyMe] = useState({
    key: 0,
    isOpen: false,
    product: null,
    selectedOptionIndex: 0,
    isAuthenticated: false,
    email: undefined,
    name: undefined
  })

  const [filterOptions, setFilterOptions] = useState()
  const [productsFiltered, setProductsFiltered] = useState([])
  const [sortOptions, setSortOptions] = useState()

  const toastContext = ToastContext.getContextActions()
  const popupToast = toastContext.popupToast
  const dismissToast = toastContext.dismissToast

  const listenLoginSuccessEvent = (kill = false) => {
    const e = constants.REACT_LOGIN_SUCCESS_EVENT
    const f = loadData
    if (kill) {
      window.removeEventListener(e, f)
    } else {
      window.addEventListener(e, f)
    }
  }

  useEffect(() => {
    api.setErrorTranslations(translations)
    loadData()
    // function to be used by Drupal to update
    // product list when category changes
    window.reactProductUpdate = loadData
  }, [])

  useEffect(() => {
    listenLoginSuccessEvent()
    return () => {
      listenLoginSuccessEvent(true)
    }
  }, [listenLoginSuccessEvent])

  if (translations) {
    setTranslations(translations)
  }

  const handleDocomoPromo = () => {
    dismissToast()
  }

  const loadData = async () => {
    await getProductsExtraData(category, true)
    analyticsProductListImpression(products)
    setFiltersInfo()
    const docomoPromo = localstorage.get('docomoPromo')
    if (docomoPromo) {
      localstorage.remove('docomoPromo')
      popupToast({
        type: ToastContext.types.success,
        autoDismiss: false,
        onClose: () => handleDocomoPromo(),
        title: translations.tk_docomoPromoDiscountToastTitle,
        subtitle: translations.tk_docomoPromoDiscountToastMessage,
        actions: [
          <Button
            key={0}
            fullWidth
            variant={Button.variants.primary}
            text={translations.tk_docomoPromoDiscountToastMessageButton}
            onClickHandler={handleDocomoPromo}
          />
        ]
      })
    }
  }

  const getProductsExtraData = async (category, showPromotions = false) => {
    setLoadingExtraData(true)
    let res = null
    try {
      res = await api.cart.getProducts({
        params: { categoryid: category, stock: true, promo: true }
      })
    } catch (err) {
      if (err.data && err.data.data && err.data.data.products) {
        res = err.data.data
      } else {
        logError(err)
        return
      }
    }
    if (res) {
      const productsList = fillProductsWithExtraData(products, res.products)
      setProducts(productsList)
      if (showPromotions) {
        dispatchPopupPromotionAlreadyUsed(
          cookie.getSpiceId(),
          productsList,
          popupToast,
          translations,
          Container,
          TextBody
        )
      }
    }
    setLoadingExtraData(false)
  }

  const setFiltersInfo = () => {
    if (!filtersInfo.hideFilters) {
      setFilterOptions(filtersInfo.filterOptions || [])
      setSortOptions(filtersInfo.sortOptions || [])
    }
  }

  const analyticsProductListImpression = products => {
    if (products && products.length) {
      analytics.productListImpression(products, listname)
    }
  }

  const handleOptionChange = (product, selectedOptionIndex) => {
    const index = products.map(p => p.id).indexOf(product.id)
    if (index >= 0) {
      const updatedProducts = [...products]
      updatedProducts[index].selectedOptionIndex = selectedOptionIndex
      setProducts(updatedProducts)
    }
  }

  const handleGoToCart = familyWarining => {
    familyWarining && analytics.productFamilyConflictDecision(true)
    goTo(Routes.cart, true)
  }

  const handleKeepShopping = familyWarining => {
    familyWarining && analytics.productFamilyConflictDecision(false)
    dismissToast()
  }

  const cartFreeShippingToastContent = msg => {
    return (
      <Container display={Container.displays.flex} pb="2">
        <Container pr="2">
          <Icon size={Icon.sizes.s} image={Icon.images.orders} />
        </Container>
        <TextBody size={TextBody.sizes.s} acceptsHtml value={msg} />
      </Container>
    )
  }

  const handleAddToCart = async (product, selectedOptionIndex) => {
    const token = cookie.getToken()
    const decodedToken = JWTDecode(token)
    if (
      decodedToken &&
      decodedToken.has_device_registered === 'false' &&
      product.isRequiredDeviceRegistration
    ) {
      popupToast({
        type: ToastContext.types.error,
        subtitle: translations.tk_productNeedsRegisteredDevices
      })
      return false
    }
    try {
      const selectedOption = product.options[selectedOptionIndex]
      const res = await api.cart.addToCart([
        { id: product.id, qty: 1, option: selectedOption }
      ])
      updateCartCounter(res.amount)
      const showFamilyWarning = res.familyNotice && !cookie.getFamilyWarning()
      if (showFamilyWarning) {
        cookie.setFamilyWarning(true)
        analytics.productFamilyConflict(false)
      }
      popupToast({
        disclaimer: res.promotionDisclaimer,
        type: res.error ? ToastContext.types.error : ToastContext.types.none,
        title: res.error
          ? translations.tk_addedToCartError
          : translations.tk_addedToCart,
        subtitle: product.options[selectedOptionIndex].variantName,
        content: (
          <Container>
            {res.free_shipping_message &&
              cartFreeShippingToastContent(res.free_shipping_message)}
            {showFamilyWarning && (
              <TextBody
                size={TextBody.sizes.s}
                {...htmlTranslation(translations, 'tk_addedToCartFamilyNotice')}
              />
            )}
            <TextBody size={TextBody.sizes.s}>
              {getToastMessagesFromResponse(res)}
            </TextBody>
          </Container>
        ),
        actions: [
          <Button
            key={0}
            fullWidth
            variant={Button.variants.secondary}
            text={translations.tk_toastKeepShopping}
            onClickHandler={() => handleKeepShopping(showFamilyWarning)}
            data-testing="ProductList_Button_KeepShopping"
          />,
          <Button
            key={1}
            fullWidth
            variant={Button.variants.primary}
            text={translations.tk_toastProceedToCart}
            onClickHandler={() => handleGoToCart(showFamilyWarning)}
            data-testing="ProductList_Button_GoToCart"
          />
        ]
      })

      getProductsExtraData(category)
    } catch (err) {
      const errorCode = extractErrorCode(err)
      const isPLError = isPurchaseLimitationsErrorCode(errorCode)
      if (isPLError) {
        analytics.addToCartLimitations(
          product &&
            product.options &&
            product.options[selectedOptionIndex].variantId,
          product &&
            product.options &&
            product.options[selectedOptionIndex].variantName,
          isCartPurchaseLimitationsErrorCode(errorCode)
        )
      }
      popupToast({
        type: ToastContext.types.error,
        title: translations.tk_addedToCartError,
        subtitle:
          product &&
          product.options &&
          product.options[selectedOptionIndex].variantName,
        content: (
          <Container>
            <TextBody
              size={TextBody.sizes.s}
              acceptsHtml
              value={getToastMessagesFromError(err)}
            />
            {isPLError && (
              <TextBody
                size={TextBody.sizes.s}
                {...htmlTranslation(
                  translations,
                  'tk_purchaseLimitationsErrorTermsAndConditions'
                )}
              />
            )}
          </Container>
        )
      })
    }
  }

  const handleAddToPreorder = async (product, selectedOptionIndex) => {
    if (isAuthenticated()) {
      try {
        const profile = await api.profile.getProfile()
        const stockNotification = await api.cart.getStockNotification(product)
        const alreadySubscribed = checkStockNotification(
          product,
          selectedOptionIndex,
          stockNotification
        )
        if (alreadySubscribed) {
          popupToast({
            type: ToastContext.types.none,
            subtitle: translations.tk_notifyMeAlreadySubscribedTitle
          })
        } else {
          setModalNotifyMe({
            key: Math.random(),
            product: product,
            selectedOptionIndex: selectedOptionIndex,
            isAuthenticated: true,
            email: profile.EmailAddress,
            name: profile.LastName + ' ' + profile.FirstName,
            isOpen: true
          })
        }
      } catch (err) {
        popupToast({
          type: ToastContext.types.error,
          subtitle: translations.tk_addedToPreorderError
        })
      }
    } else {
      setModalNotifyMe({
        key: Math.random(),
        product: product,
        selectedOptionIndex: selectedOptionIndex,
        isAuthenticated: false,
        email: undefined,
        name: undefined,
        isOpen: true
      })
    }
  }

  const handleOnNotifyMe = async (
    userInfo,
    product,
    selectedOptionIndex,
    recaptchaData
  ) => {
    const selectedOption = product.options[selectedOptionIndex]
    try {
      await api.cart.addStockNotification(
        userInfo,
        product.id,
        selectedOption,
        recaptchaData
      )
      return {
        success: true
      }
    } catch (err) {
      const code =
        err.data &&
        Array.isArray(err.data) &&
        err.data.length > 0 &&
        err.data[0].code
      if (
        code === constants.CAPTCHA_VALIDATION_ERROR_01 ||
        code === constants.CAPTCHA_VALIDATION_ERROR_02
      ) {
        return {
          success: false,
          captchaError: true
        }
      } else {
        resetGrecaptcha(constants.STOCK_NOTIFICATION_GRECAPTCHA_ACTION)
        popupToast({
          type: ToastContext.types.error,
          subtitle: translations.tk_addedToPreorderError
        })
        return {
          success: false,
          captchaError: false,
          error:
            err.data &&
            Array.isArray(err.data) &&
            err.data.length > 0 &&
            err.data[0].message
        }
      }
    }
  }

  const getProductsToPresent = () => {
    // If some filter is applied or sorted, show the filtered products list, otherwise show all products
    return productsFiltered && productsFiltered.length
      ? productsFiltered
      : products && products.length
      ? products
      : []
  }

  const displayList = (slice1, slice2) => {
    if (!products) {
      return null
    }

    const productsToPresent = getProductsToPresent()

    const columns = [1, 2, 3]
    return (
      <ListContainer columns={columns} xBetween="8" yBetween="20" fullWidth>
        {(productsToPresent.slice(slice1, slice2) || []).map(
          (product, index) =>
            product.options && (
              <Container
                key={product.id}
                display={Container.displays.flex}
                flexDirection={Container.flexDirections.column}
                fullWidth>
                <ProductCardProduct
                  onAddToCart={handleAddToCart}
                  onAddToPreorder={handleAddToPreorder}
                  onOptionChange={handleOptionChange}
                  translations={translations}
                  productPosition={index}
                  product={product}
                  mainCategoryName={mainCategory.category}
                  loading={loadingExtraData}
                />
                {/** To help only for tests */}
                {canUseDOM && window.filters_debug ? (
                  <Container>
                    {'Attributes: '}
                    {JSON.stringify(product.optionsAttributes)}
                  </Container>
                ) : null}
              </Container>
            )
        )}
      </ListContainer>
    )
  }

  let cut = 0
  if (getProductsToPresent().length) {
    const columns = [1, 2, 3]
    const currentBreakpointIndex = Math.min(
      columns.length - 1,
      Tools.getCurrentBreakpointIndex()
    )
    if (currentBreakpointIndex !== null) {
      const elementsPerRow = columns[currentBreakpointIndex]
      const middleLine = Math.ceil(
        getProductsToPresent().length / elementsPerRow / 2
      )
      cut = Math.min(middleLine * elementsPerRow, getProductsToPresent().length)
    } else {
      setSecondHalf(false)
    }
  }

  return (
    <>
      <NoSsr>
        <BazaarVoice.OverrideStyle />
      </NoSsr>
      <Container
        display={Container.displays.flex}
        flexDirection={[
          Container.flexDirections.column,
          Container.flexDirections.column,
          Container.flexDirections.row
        ]}>
        {!filtersInfo.hideFilters ? (
          <Container
            width={['full', 'full', '3/12']}
            pr={['0', '0', '8']}
            pb={['10', '10', '0']}>
            <ProductListFilters
              translations={translations}
              filterOptions={filterOptions}
              sortOptions={sortOptions}
              originalList={products}
              setListFiltered={setProductsFiltered}
            />
          </Container>
        ) : null}
        <Container
          width={filtersInfo.hideFilters ? 'full' : ['full', 'full', '9/12']}>
          <Container
            display={Container.displays.flex}
            flexDirection={Container.flexDirections.column}
            justifyContent={Container.justifyContentValues.center}
            fullWidth>
            {getProductsToPresent().length ? (
              <Container fullWidth>
                <Container pb="4">
                  <TextTitle
                    heading={TextTitle.headings.h3}
                    data-testing="ProductList_TextTitle_ListTitle">
                    {`${(mainCategory && mainCategory.category) || ''} (${
                      getProductsToPresent().length || 0
                    })`}
                  </TextTitle>
                </Container>
                <Container pb="14">
                  <TextBody
                    style={TextBody.styles.strong}
                    size={TextBody.sizes.small}
                    acceptsHtml
                    value={(mainCategory && mainCategory.description) || ''}
                  />
                </Container>
                <ListContainer yBetween="20" columns={1}>
                  {secondHalf
                    ? displayList(0, cut)
                    : displayList(0, getProductsToPresent().length)}
                  {listBanner && (
                    <Container fullWidth>
                      <HtmlContent content={listBanner} />
                    </Container>
                  )}
                  {secondHalf
                    ? displayList(cut, getProductsToPresent().length)
                    : null}
                </ListContainer>
              </Container>
            ) : (
              <Container
                fullWidth
                display={Container.displays.flex}
                justifyContent={Container.justifyContentValues.center}
                py="20">
                <TextBody
                  {...htmlTranslation(translations, 'tk_noProductsFound')}
                />
              </Container>
            )}
          </Container>
        </Container>
      </Container>
      <ModalNotifyMe
        key={modalNotifyMe.key}
        isAuthenticated={modalNotifyMe.isAuthenticated}
        defaultEmail={modalNotifyMe.email}
        defaultName={modalNotifyMe.name}
        translations={translations}
        isOpen={modalNotifyMe.isOpen}
        product={modalNotifyMe.product}
        selectedOptionIndex={modalNotifyMe.selectedOptionIndex}
        handleOnNotifyMe={handleOnNotifyMe}
        onClose={() => {
          setModalNotifyMe({
            ...modalNotifyMe,
            ...{ isOpen: false }
          })
        }}
      />
    </>
  )
}

const ProductList = props => {
  return (
    <ToastContext>
      <PublishedComponent
        innerComponent={ProductListComponent}
        {...ComponentDefaultProps}
        {...props}
      />
    </ToastContext>
  )
}

const ComponentTranslations = {
  ...ProductListTranslations,
  ...CommonTranslations
}

const ComponentDefaultProps = {
  componentName: 'ProductList',
  translations: {
    ...parseTranslations(ComponentTranslations, true)
  }
}

ProductList.getInitialProps = async req => {
  return PublishedComponent.getGlobalInitialProps({
    context: req && req.query && req.query.context,
    componentName: ComponentDefaultProps.componentName,
    defaultTranslations: ComponentDefaultProps.translations,
    translationsConfig: ComponentTranslations,
    getSpecificInitialProps: async () => {
      let res = {}
      let error = null
      const hideFilters = req.query.hide_filters === 'true'
      try {
        res = await ProductList.getProducts(
          req.query.category,
          req,
          hideFilters
        )
      } catch (err) {
        error = {
          message: extractErrorMessage(err),
          status: err.status
        }
        if (err.data && err.data.data && err.data.data.products) {
          res = ProductList.getProductsInfo(err.data.data, req)
        }
      }
      return {
        error,
        category: req.query.category,
        defaultProducts: res.products || [],
        listname: (req.body.extra && req.body.extra.ga_list) || null,
        listBanner: (req.body.extra && req.body.extra.plpBanners) || null,
        mainCategory: res.mainCategory || {},
        filtersInfo: res.filtersInfo || { hideFilters }
      }
    }
  })
}

ProductList.getProducts = async (category, req, hideFilters) => {
  const res = await api.cart.getProducts({
    req,
    params: {
      categoryid: category,
      ...(!hideFilters && { filterOptions: true, sortOptions: true })
    }
  })
  return ProductList.getProductsInfo(res, req, hideFilters)
}

ProductList.getProductsInfo = async (res, req, hideFilters) => {
  const productList = (req.body.extra && req.body.extra.product_list) || null
  const products = mapProductListForDisplay(res.products, true, productList)
  return {
    products: products || [],
    mainCategory: res.main_category || [],
    filtersInfo: {
      hideFilters,
      filterOptions: res.filterOptions || [],
      sortOptions: res.sortOptions || []
    }
  }
}

export default ProductList
